/*
 * Log de auditoria: quem fez o que, quando e de onde.
 *
 * Somente leitura, e não por falta de tempo — registro que a própria aplicação
 * pode alterar ou apagar não serve para auditar coisa alguma. Não existe rota
 * de alteração no backend.
 *
 * O conteúdo de antes/depois já passou por redaction na gravação: CPF,
 * telefone, senha e token nunca chegaram ao banco.
 */
import { Fragment, useEffect, useState } from 'react';
import { PERMISSIONS } from '@hotspot/shared';
import { ApiRequestError, apiList, apiRequest } from '../lib/api';
import { useAuth } from '../lib/auth';
import type { AuditEntryDto, AuditOptionsDto } from '../lib/types';
import { Etiqueta, dataCurta } from './CustomersPage';
import { Alert, Button, Card, Spinner } from '../components/ui';

/**
 * Tradução das ações. O que não estiver aqui aparece como veio — melhor um
 * identificador técnico na tela do que uma ação nova ficar invisível.
 */
const ROTULO_ACAO: Record<string, string> = {
  'auth.login': 'Entrou no painel',
  'auth.login_failed': 'Tentativa de login falhou',
  'auth.logout': 'Saiu do painel',
  'auth.password_changed': 'Trocou a senha',
  'admin.created': 'Criou administrador',
  'admin.updated': 'Alterou administrador',
  'admin.password_reset': 'Redefiniu senha de administrador',
  'unit.created': 'Criou unidade',
  'unit.updated': 'Alterou unidade',
  'unit.deleted': 'Removeu unidade',
  'mikrotik.created': 'Cadastrou MikroTik',
  'mikrotik.updated': 'Alterou MikroTik',
  'mikrotik.deleted': 'Removeu MikroTik',
  'mikrotik.provisioned': 'Provisionou HTTPS no MikroTik',
  'mikrotik.hotspot_provisioned': 'Provisionou o hotspot',
  'mikrotik.portal_provisioned': 'Enviou o portal ao MikroTik',
  'mikrotik.fingerprint_trusted': 'Confiou no certificado do MikroTik',
  'campaign.created': 'Criou campanha',
  'campaign.updated': 'Alterou campanha',
  'campaign.deleted': 'Removeu campanha',
  'portal_theme.updated': 'Alterou a aparência do portal',
  'portal_theme.reset': 'Removeu personalização de aparência',
  'hotspot_config.updated': 'Alterou o modo de acesso',
  'hotspot_config.reset': 'Removeu configuração de acesso',
  'terms.published': 'Publicou termos',
  'terms.activated': 'Reativou versão de termos',
  'customer.blocked': 'Bloqueou cliente',
  'customer.unblocked': 'Liberou cliente',
  'customer.anonymized': 'Anonimizou cliente (LGPD)',
  'customer.exported_csv': 'Exportou lista de clientes',
  'customer.exported_data': 'Exportou dados de um cliente',
  'customer.recognition_reset': 'Exigiu novo cadastro do cliente',
  'session.disconnected': 'Desconectou cliente',
  'session.authorized_manually': 'Liberou acesso manualmente',
  'device.blocked': 'Bloqueou aparelho',
  'device.unblocked': 'Liberou aparelho',
  'report.exported_accesses': 'Exportou histórico de acessos',
};

/** Ações que merecem destaque: mexem em acesso, em dado pessoal ou em conta. */
const SENSIVEIS = new Set([
  'customer.anonymized',
  'customer.exported_csv',
  'customer.exported_data',
  'customer.blocked',
  'device.blocked',
  'session.disconnected',
  'session.authorized_manually',
  'admin.created',
  'admin.password_reset',
  'auth.login_failed',
  'mikrotik.fingerprint_trusted',
]);

const JANELAS = [
  { dias: 1, rotulo: 'Hoje' },
  { dias: 7, rotulo: '7 dias' },
  { dias: 30, rotulo: '30 dias' },
  { dias: 365, rotulo: 'Tudo' },
] as const;

export function AuditPage() {
  const { can } = useAuth();
  const podeVer = can(PERMISSIONS.AUDIT_READ);

  const [registros, setRegistros] = useState<AuditEntryDto[] | null>(null);
  const [total, setTotal] = useState(0);
  const [opcoes, setOpcoes] = useState<AuditOptionsDto | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const [dias, setDias] = useState<number>(7);
  const [acao, setAcao] = useState('');
  const [ator, setAtor] = useState('');
  const [aberto, setAberto] = useState<number | null>(null);

  useEffect(() => {
    if (!podeVer) return;
    apiRequest<AuditOptionsDto>('/admin/audit/options')
      .then(setOpcoes)
      .catch(() => setOpcoes(null));
  }, [podeVer]);

  useEffect(() => {
    if (!podeVer) return;

    let ativo = true;
    setRegistros(null);

    const partes = [`dias=${dias}`, 'perPage=100'];
    if (acao) partes.push(`action=${encodeURIComponent(acao)}`);
    if (ator) partes.push(`actorAdminId=${ator}`);

    apiList<AuditEntryDto>(`/admin/audit?${partes.join('&')}`)
      .then(({ data, meta }) => {
        if (!ativo) return;
        setRegistros(data);
        setTotal(meta?.total ?? data.length);
      })
      .catch((caught: unknown) => {
        if (!ativo) return;
        setRegistros([]);
        setErro(caught instanceof ApiRequestError ? caught.displayMessage : 'Falha ao carregar');
      });

    return () => {
      ativo = false;
    };
  }, [dias, acao, ator, podeVer]);

  if (!podeVer) {
    return <Alert tone="info">Seu perfil não tem acesso ao log de auditoria.</Alert>;
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-bold text-slate-900">Auditoria</h1>
        <p className="text-sm text-slate-500">
          Toda alteração feita pelo painel fica registrada. O log não pode ser editado nem
          apagado pelo sistema.
        </p>
      </header>

      <Card>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex rounded-lg border border-slate-200 p-0.5 text-sm">
            {JANELAS.map((janela) => (
              <button
                key={janela.dias}
                type="button"
                onClick={() => setDias(janela.dias)}
                className={`rounded-md px-3 py-1.5 font-medium transition-colors ${
                  dias === janela.dias
                    ? 'bg-brand-600 text-white'
                    : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                {janela.rotulo}
              </button>
            ))}
          </div>

          {opcoes && opcoes.actions.length > 0 ? (
            <label className="flex items-center gap-2 text-sm text-slate-600">
              Ação
              <select
                value={acao}
                onChange={(evento) => setAcao(evento.target.value)}
                className="max-w-[260px] rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none"
              >
                <option value="">Todas</option>
                {opcoes.actions.map((valor) => (
                  <option key={valor} value={valor}>
                    {ROTULO_ACAO[valor] ?? valor}
                  </option>
                ))}
              </select>
            </label>
          ) : null}

          {opcoes && opcoes.actors.length > 1 ? (
            <label className="flex items-center gap-2 text-sm text-slate-600">
              Quem
              <select
                value={ator}
                onChange={(evento) => setAtor(evento.target.value)}
                className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none"
              >
                <option value="">Todos</option>
                {opcoes.actors.map((pessoa) => (
                  <option key={pessoa.id} value={pessoa.id}>
                    {pessoa.label}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
        </div>
      </Card>

      {erro ? <Alert>{erro}</Alert> : null}

      <Card title={registros ? `${total} ${total === 1 ? 'registro' : 'registros'}` : 'Registros'}>
        {!registros ? (
          <Spinner />
        ) : registros.length === 0 ? (
          <p className="text-sm text-slate-500">Nenhum registro para este filtro.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-xs tracking-wide text-slate-500 uppercase">
                  <th className="py-2 pr-3">Quando</th>
                  <th className="py-2 pr-3">Quem</th>
                  <th className="py-2 pr-3">O que</th>
                  <th className="py-2 pr-3">Alvo</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {registros.map((registro) => (
                  /* Fragment nomeado, e nao <>: cada item da lista precisa de
                     key no elemento mais externo, e o fragmento curto nao aceita. */
                  <Fragment key={registro.id}>
                    <tr className="border-b border-slate-100 last:border-0">
                      <td className="py-2.5 pr-3 whitespace-nowrap text-slate-600">
                        {dataCurta(registro.createdAt)}
                      </td>
                      <td className="py-2.5 pr-3 text-slate-700">
                        {registro.actorLabel ?? (
                          <Etiqueta tom="slate">
                            {registro.actorType === 'system' ? 'sistema' : registro.actorType}
                          </Etiqueta>
                        )}
                      </td>
                      <td className="py-2.5 pr-3">
                        <span className="text-slate-800">
                          {ROTULO_ACAO[registro.action] ?? registro.action}
                        </span>
                        {SENSIVEIS.has(registro.action) ? (
                          <span className="ml-2">
                            <Etiqueta tom="red">sensível</Etiqueta>
                          </span>
                        ) : null}
                      </td>
                      <td className="py-2.5 pr-3 text-xs text-slate-500">
                        {registro.entityType}
                        {registro.entityId ? ` #${registro.entityId}` : ''}
                      </td>
                      <td className="py-2.5 text-right">
                        {registro.before || registro.after ? (
                          <Button
                            variant="ghost"
                            onClick={() =>
                              setAberto(aberto === registro.id ? null : registro.id)
                            }
                          >
                            {aberto === registro.id ? 'Ocultar' : 'Detalhes'}
                          </Button>
                        ) : null}
                      </td>
                    </tr>

                    {aberto === registro.id ? (
                      <tr className="border-b border-slate-100">
                        <td colSpan={5} className="bg-slate-50 px-3 py-3">
                          <div className="grid gap-3 sm:grid-cols-2">
                            <Json titulo="Antes" valor={registro.before} />
                            <Json titulo="Depois" valor={registro.after} />
                          </div>
                          {registro.ip ? (
                            <p className="mt-2 text-xs text-slate-500">
                              Origem: <code className="font-mono">{registro.ip}</code>
                            </p>
                          ) : null}
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function Json({ titulo, valor }: { titulo: string; valor: unknown }) {
  if (valor === null || valor === undefined) return null;

  return (
    <div>
      <p className="mb-1 text-xs font-medium tracking-wide text-slate-500 uppercase">{titulo}</p>
      <pre className="max-h-52 overflow-auto rounded bg-white p-2 text-xs whitespace-pre-wrap text-slate-700">
        {JSON.stringify(valor, null, 2)}
      </pre>
    </div>
  );
}
